import { recordStatusLabel } from "../../domain/shared/status-labels.js";
import { router } from "../router/router.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { contextStore } from "../auth/context-store.js";
import { NormativeCategory } from "../../catalogs/normative-reference.js";

const template = document.createElement("template");
template.innerHTML = `
  <div class="catalogs-page" style="display: flex; flex-direction: column; height: 100%; gap: var(--space-4); background: var(--color-graphite-50); padding: var(--space-6);">
    <header style="display: flex; justify-content: space-between; align-items: center;">
      <div>
        <h1 style="color: var(--color-text-muted); font-size: var(--font-size-2xl); margin: 0;">Catálogos e Estrutura Normativa</h1>
        <p style="color: var(--color-graphite-400); font-size: var(--font-size-sm); margin-top: 4px;">Gerenciamento de catálogos core industriais e referências normativas do sistema.</p>
      </div>
      <button id="btn-new-item" style="background-color: var(--color-primary); color: var(--color-text-primary); border: none; padding: var(--space-2) var(--space-4); border-radius: var(--radius-md); font-weight: 500; cursor: pointer; font-size: var(--font-size-sm);">
        + Novo Registro
      </button>
    </header>

    <!-- Tabs -->
    <div style="display: flex; gap: var(--space-2); border-bottom: 2px solid var(--color-graphite-200);">
      <button id="tab-core" class="tab-btn active" style="padding: var(--space-2) var(--space-4); background: none; border: none; border-bottom: 3px solid var(--color-primary); color: var(--color-text-muted); font-weight: 600; cursor: pointer;">
        Catálogos Core
      </button>
      <button id="tab-normative" class="tab-btn" style="padding: var(--space-2) var(--space-4); background: none; border: none; border-bottom: 3px solid transparent; color: var(--color-text-secondary); font-weight: 500; cursor: pointer;">
        Referências Normativas
      </button>
    </div>

    <!-- Filters -->
    <div style="display: flex; gap: var(--space-4); background: var(--color-surface-primary); padding: var(--space-4); border-radius: var(--radius-md); border: 1px solid var(--color-graphite-300);">
      <select id="filter-catalog" style="padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
        <option value="DISCIPLINE">DISCIPLINE (Disciplinas)</option>
        <option value="CRITICALITY">CRITICALITY (Criticidade)</option>
        <option value="EXECUTION_WINDOW">EXECUTION_WINDOW (Janela de Execução)</option>
        <option value="EQUIPMENT_TYPE">EQUIPMENT_TYPE (Tipos de Equipamento)</option>
        <option value="INSPECTION_MODALITY">INSPECTION_MODALITY (Modalidade)</option>
        <option value="INSPECTION_NATURE">INSPECTION_NATURE (Natureza)</option>
        <option value="DOCUMENT_STATUS">DOCUMENT_STATUS (Status Documental)</option>
        <option value="NDT_METHOD">NDT_METHOD (Métodos END)</option>
      </select>

      <select id="filter-category" style="display: none; padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
        <option value="">Todas as Categorias</option>
        <option value="IN_SERVICE_INSPECTION_CODE">Inspeção em Serviço (ex: API 510/570/653)</option>
        <option value="RECOMMENDED_PRACTICE">Prática Recomendada (ex: API RP 572/574/575/576)</option>
        <option value="DESIGN_CONSTRUCTION_CODE">Projeto e Construção (ex: ASME Sec VIII, B31.3, API 650)</option>
        <option value="REGULATORY_REQUIREMENT">Requisito Regulatório (ex: NR-13)</option>
        <option value="TEST_METHOD_STANDARD">Norma de Método de Ensaio</option>
        <option value="PROCEDURE_REFERENCE">Procedimento de Referência</option>
        <option value="OTHER">Outros</option>
      </select>

      <select id="filter-status" style="padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
        <option value="">Todos os Status</option>
        <option value="ACTIVE" selected>Ativos</option>
        <option value="INACTIVE">Inativos</option>
      </select>

      <input type="text" id="filter-search" placeholder="Buscar por código ou descrição..." style="flex: 1; padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
    </div>

    <!-- Data Table -->
    <div style="flex: 1; background: var(--color-surface-primary); border-radius: var(--radius-md); border: 1px solid var(--color-graphite-300); overflow-y: auto;">
      <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: var(--font-size-sm);">
        <thead style="background: var(--color-graphite-50); border-bottom: 1px solid var(--color-graphite-300); sticky: top;">
          <tr id="table-header">
            <th style="padding: var(--space-3); color: var(--color-text-secondary);">Código</th>
            <th style="padding: var(--space-3); color: var(--color-text-secondary);">Rótulo / Título</th>
            <th style="padding: var(--space-3); color: var(--color-text-secondary);">Descrição / Org</th>
            <th style="padding: var(--space-3); color: var(--color-text-secondary);">Status</th>
            <th style="padding: var(--space-3); color: var(--color-text-secondary);">Ações</th>
          </tr>
        </thead>
        <tbody id="table-body"></tbody>
      </table>
    </div>
  </div>
`;

export default class CatalogsPage extends HTMLElement {
  private activeTab: "CORE" | "NORMATIVE" = "CORE";

  public constructor() {
    super();
    this.appendChild(template.content.cloneNode(true));
  }

  public connectedCallback(): void {
    if (!contextStore.hasPermission(PermissionCode.CatalogRead)) {
      router.navigate("/");
      return;
    }

    this.setupTabListeners();
    this.renderView();
  }

  private setupTabListeners() {
    const tabCore = this.querySelector("#tab-core") as HTMLButtonElement;
    const tabNormative = this.querySelector("#tab-normative") as HTMLButtonElement;
    const filterCatalog = this.querySelector("#filter-catalog") as HTMLSelectElement;
    const filterCategory = this.querySelector("#filter-category") as HTMLSelectElement;

    tabCore?.addEventListener("click", () => {
      this.activeTab = "CORE";
      tabCore.style.borderBottomColor = "var(--color-primary)";
      tabCore.style.fontWeight = "600";
      tabCore.style.color = "var(--color-graphite-700)";
      
      tabNormative.style.borderBottomColor = "transparent";
      tabNormative.style.fontWeight = "500";
      tabNormative.style.color = "var(--color-graphite-500)";

      filterCatalog.style.display = "block";
      filterCategory.style.display = "none";
      this.renderView();
    });

    tabNormative?.addEventListener("click", () => {
      this.activeTab = "NORMATIVE";
      tabNormative.style.borderBottomColor = "var(--color-primary)";
      tabNormative.style.fontWeight = "600";
      tabNormative.style.color = "var(--color-graphite-700)";

      tabCore.style.borderBottomColor = "transparent";
      tabCore.style.fontWeight = "500";
      tabCore.style.color = "var(--color-graphite-500)";

      filterCatalog.style.display = "none";
      filterCategory.style.display = "block";
      this.renderView();
    });

    this.querySelector("#filter-catalog")?.addEventListener("change", () => this.renderView());
    this.querySelector("#filter-category")?.addEventListener("change", () => this.renderView());
    this.querySelector("#filter-status")?.addEventListener("change", () => this.renderView());
    this.querySelector("#filter-search")?.addEventListener("input", () => this.renderView());
  }

  private renderView() {
    const tbody = this.querySelector("#table-body")!;
    tbody.innerHTML = "";

    // Simulate backend fetch
    Promise.resolve().then(() => {
      if (this.activeTab === "CORE") {
        const sampleCoreItems = [
        { code: "MECHANICAL", label: "Mecânica", desc: "Engenharia e manutenção mecânica", status: "ACTIVE" },
        { code: "ELECTRICAL", label: "Elétrica", desc: "Sistemas elétricos", status: "ACTIVE" },
        { code: "INSTRUMENTATION", label: "Instrumentação", desc: "Instrumentação e automação", status: "ACTIVE" },
        { code: "CIVIL", label: "Civil / Estrutural", desc: "Estruturas civis e fundações", status: "ACTIVE" },
        { code: "INSPECTION", label: "Inspeção", desc: "Inspeção de equipamentos e integridade", status: "ACTIVE" }
      ];

      sampleCoreItems.forEach(item => {
        const tr = document.createElement("tr");
        tr.style.borderBottom = "1px solid var(--color-graphite-200)";
        tr.innerHTML = `
          <td style="padding: var(--space-3); font-family: monospace; font-weight: 600; color: var(--color-text-muted);">${item.code}</td>
          <td style="padding: var(--space-3); color: var(--color-text-muted);">${item.label}</td>
          <td style="padding: var(--space-3); color: var(--color-text-secondary);">${item.desc}</td>
          <td style="padding: var(--space-3);">
            <span style="background: rgba(16, 185, 129, 0.1); color: var(--status-success-text); padding: 2px 8px; border-radius: 12px; font-size: var(--font-size-xs); font-weight: 500;">${recordStatusLabel(item.status)}</span>
          </td>
          <td style="padding: var(--space-3);">
            <button style="background: none; border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 2px 8px; font-size: var(--font-size-xs); cursor: pointer; color: var(--color-text-secondary);">Editar</button>
          </td>
        `;
        tbody.appendChild(tr);
      });
    } else {
      const sampleNormativeRefs = [
        { code: "API_510", title: "Pressure Vessel Inspection Code", org: "API", category: "IN_SERVICE_INSPECTION_CODE", edition: "Não informada", status: "ACTIVE" },
        { code: "API_570", title: "Piping Inspection Code", org: "API", category: "IN_SERVICE_INSPECTION_CODE", edition: "Não informada", status: "ACTIVE" },
        { code: "API_653", title: "Tank Inspection, Repair, Alteration, and Reconstruction", org: "API", category: "IN_SERVICE_INSPECTION_CODE", edition: "Não informada", status: "ACTIVE" },
        { code: "API_RP_572", title: "Inspection Practices for Pressure Vessels", org: "API", category: "RECOMMENDED_PRACTICE", edition: "Não informada", status: "ACTIVE" },
        { code: "ASME_SEC_VIII", title: "ASME Boiler and Pressure Vessel Code, Section VIII", org: "ASME", category: "DESIGN_CONSTRUCTION_CODE", edition: "Não informada", status: "ACTIVE" },
        { code: "NR_13", title: "Caldeiras, Vasos de Pressão, Tubulações e Tanques Metálicos", org: "MTE", category: "REGULATORY_REQUIREMENT", edition: "Não informada", status: "ACTIVE" }
      ];

      sampleNormativeRefs.forEach(ref => {
        const tr = document.createElement("tr");
        tr.style.borderBottom = "1px solid var(--color-graphite-200)";
        tr.innerHTML = `
          <td style="padding: var(--space-3); font-family: monospace; font-weight: 600; color: var(--color-text-muted);">${ref.code}</td>
          <td style="padding: var(--space-3); color: var(--color-text-muted);">${ref.title}</td>
          <td style="padding: var(--space-3); color: var(--color-text-secondary);">${ref.org} (${ref.category})</td>
          <td style="padding: var(--space-3);">
            <span style="background: rgba(16, 185, 129, 0.1); color: var(--status-success-text); padding: 2px 8px; border-radius: 12px; font-size: var(--font-size-xs); font-weight: 500;">${recordStatusLabel(ref.status)}</span>
          </td>
          <td style="padding: var(--space-3);">
            <button style="background: none; border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 2px 8px; font-size: var(--font-size-xs); cursor: pointer; color: var(--color-text-secondary);">Editar</button>
          </td>
        `;
        tbody.appendChild(tr);
      });
      }
    });
  }
}

if (!customElements.get("app-catalogs-page")) {
  customElements.define("app-catalogs-page", CatalogsPage);
}


